// Step 5 của Phase 10 — modify ~/.claude/mcp_servers.json add entry GitNexus
// để Claude Code attach MCP server lúc start.
//
// Behavior:
//   - File chưa tồn tại → tạo mới với entry gitnexus duy nhất
//   - File có entries khác (vd tools, secrets) → merge giữ
//   - File có entry gitnexus đúng → no-op (idempotent)
//   - File có entry gitnexus khác → override (Q4 decision) + backup file cũ
//
// Atomic write qua tmp + rename + chmod 600 (consistent với pattern Avatar settings).
import { promises as fs } from "node:fs";
import { homedir } from "node:os";
import { join } from "node:path";
import { pathExists, readJson, writeJsonAtomic } from "./filesystem-helpers.js";
import { log } from "./terminal-logger.js";

// chmod 600 — consistent với .claude/settings.json pattern (chứa OAuth-like data).
const MCP_FILE_MODE = 0o600;

// Entry expected mà Avatar muốn enforce — match GitNexus official MCP server spec.
const EXPECTED_GITNEXUS_ENTRY = {
  command: "gitnexus",
  args: ["mcp"],
} as const;

export interface RegisterMcpResult {
  path: string; // ~/.claude/mcp_servers.json
  wasUpdated: boolean; // false nếu entry đã đúng (no-op)
  backup?: string; // path backup nếu file existed + cần modify
}

// Shape minimal của mcp_servers.json — index signature giữ key user khác.
interface McpServersConfig {
  mcp_servers?: Record<string, unknown>;
  [k: string]: unknown;
}

// Get path ~/.claude/mcp_servers.json cross-platform.
function getMcpServersPath(): string {
  return join(homedir(), ".claude", "mcp_servers.json");
}

// Deep equal cho 2 plain objects (đủ dùng cho entry compare, không cần full lodash).
function isEntryEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== "object" || typeof b !== "object" || a === null || b === null) return false;
  return JSON.stringify(a) === JSON.stringify(b);
}

// Backup file existing với timestamp suffix (idempotent backup name).
async function backupExistingFile(path: string): Promise<string> {
  const ts = new Date().toISOString().replace(/[:.]/g, "-");
  const backupPath = `${path}.avatar-backup-${ts}`;
  await fs.copyFile(path, backupPath);
  return backupPath;
}

// Main API — register GitNexus MCP server entry idempotent.
// Trả structured result để caller (orchestrator) log đúng action đã làm.
export async function registerGitnexusMcpServer(): Promise<RegisterMcpResult> {
  const path = getMcpServersPath();

  // Read existing (default {} nếu chưa tồn tại).
  let existing: McpServersConfig = {};
  let fileExisted = false;
  if (await pathExists(path)) {
    fileExisted = true;
    try {
      existing = await readJson<McpServersConfig>(path);
    } catch (err) {
      throw new Error(
        `MCP config corrupted (${path}): ${(err as Error).message}. Backup + xóa file để Avatar tạo lại.`,
      );
    }
  }

  // Idempotent check — nếu entry đã đúng thì no-op.
  const existingEntry = existing.mcp_servers?.gitnexus;
  if (existingEntry && isEntryEqual(existingEntry, EXPECTED_GITNEXUS_ENTRY)) {
    log.dim(`MCP entry gitnexus đã đúng tại ${path} (no-op)`);
    return { path, wasUpdated: false };
  }

  // Backup file cũ trước khi modify.
  let backup: string | undefined;
  if (fileExisted) {
    backup = await backupExistingFile(path);
    log.dim(`Backup ${path} → ${backup}`);
  }

  // Merge giữ entries khác user đã có.
  const merged: McpServersConfig = {
    ...existing,
    mcp_servers: {
      ...(existing.mcp_servers || {}),
      gitnexus: EXPECTED_GITNEXUS_ENTRY,
    },
  };

  await writeJsonAtomic(path, merged, MCP_FILE_MODE);
  // Re-chmod sau write — pattern consistent với write-claude-settings-json-per-project.
  try {
    await fs.chmod(path, MCP_FILE_MODE);
  } catch {
    // Windows / fs không support — bỏ qua.
  }

  log.success(`Registered MCP server: gitnexus → ${path}`);
  return { path, wasUpdated: true, backup };
}
